"use client"

import { useEffect, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react"
import { MdEmail, MdPerson, MdPhone, MdMessage } from "react-icons/md"
import { AiOutlineLoading3Quarters } from "react-icons/ai"
import { toast } from "sonner"
import { Button } from "@/components/ui-lab/button"
import { Input } from "@/components/ui-lab/input"
import { Label } from "@/components/ui-lab/label"
import { Textarea } from "@/components/ui-lab/textarea"
import { cn } from "@/lib/utils"

interface ContactFormData {
  name: string
  email: string
  phone?: string
  subject: string
  message: string
}

type FieldErrors = Partial<Record<keyof ContactFormData, string>>

const initialValues: ContactFormData = { name: "", email: "", phone: "", subject: "", message: "" }
const MESSAGE_MAX_LENGTH = 1000
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/

const validate = (values: ContactFormData): FieldErrors => {
  const errors: FieldErrors = {}
  const name = values.name.trim()
  const email = values.email.trim()
  const phone = values.phone?.trim()
  const subject = values.subject.trim()
  const message = values.message.trim()

  if (!name) errors.name = "Please enter your name"
  else if (name.length < 2) errors.name = "Name must be at least 2 characters"

  if (!email) errors.email = "Please enter your email"
  else if (!EMAIL_PATTERN.test(email)) errors.email = "Please enter a valid email"

  if (phone && !PHONE_PATTERN.test(phone)) errors.phone = "Please enter a valid phone number"

  if (!subject) errors.subject = "Please enter a subject"
  else if (subject.length < 3) errors.subject = "Subject must be at least 3 characters"

  if (!message) errors.message = "Please enter your message"
  else if (message.length < 10) errors.message = "Message must be at least 10 characters"

  return errors
}

type FieldProps = {
  id: keyof ContactFormData
  label: string
  required?: boolean
  error?: string
  children: ReactNode
}

const Field = ({ id, label, required, error, children }: FieldProps) => (
  <div className="space-y-2">
    <Label htmlFor={id}>
      {required && <span className="text-destructive mr-1">*</span>}
      {label}
    </Label>
    {children}
    {error && (
      <p id={`${id}-error`} className="text-sm text-destructive">
        {error}
      </p>
    )}
  </div>
)

const inputClassName = "h-10 pl-9 transition-all duration-300 hover:border-teal-500 focus-visible:border-teal-500"
const iconClassName = "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"

export const ContactForm = () => {
  const [values, setValues] = useState<ContactFormData>(initialValues)
  const [errors, setErrors] = useState<FieldErrors>({})

  const [loading, setLoading] = useState(false)
  const queueKey = "contact-form-queue"

  const enqueueSubmission = (values: ContactFormData) => {
    const existing = localStorage.getItem(queueKey)
    const queue: ContactFormData[] = existing ? JSON.parse(existing) : []
    queue.push(values)
    localStorage.setItem(queueKey, JSON.stringify(queue))
  }

  const flushQueue = async () => {
    const existing = localStorage.getItem(queueKey)
    const queue: ContactFormData[] = existing ? JSON.parse(existing) : []

    if (!queue.length) return

    for (let index = 0; index < queue.length; index += 1) {
      const payload = queue[index]

      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          return
        }
      } catch {
        return
      }
    }

    localStorage.removeItem(queueKey)
    toast.success("Queued message sent successfully.")
  }

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleOnline = () => {
      flushQueue()
    }

    window.addEventListener("online", handleOnline)
    flushQueue()

    return () => {
      window.removeEventListener("online", handleOnline)
    }
  }, [])

  const resetForm = () => {
    setValues(initialValues)
    setErrors({})
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setValues((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const validationErrors = validate(values)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setLoading(true)
    try {
      if (!navigator.onLine) {
        enqueueSubmission(values)
        toast.info("You are offline. Your message will be sent when you reconnect.")
        resetForm()
        return
      }

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })

      const data = await response.json()

      if (response.ok) {
        toast.success("Message sent successfully! I'll get back to you soon.")
        resetForm()
      } else {
        throw new Error(data.error || "Failed to send message")
      }
    } catch {
      enqueueSubmission(values)
      toast.info("Message saved offline. It will send when you are back online.")
    } finally {
      setLoading(false)
    }
  }

  const fieldProps = (id: keyof ContactFormData) => ({
    id,
    name: id,
    value: values[id] ?? "",
    onChange: handleChange,
    "aria-invalid": !!errors[id],
    "aria-describedby": errors[id] ? `${id}-error` : undefined,
  })

  return (
    <form onSubmit={handleSubmit} noValidate autoComplete="off" className="max-w-2xl space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-5">
        <Field id="name" label="Your Name" required error={errors.name}>
          <div className="relative">
            <MdPerson className={iconClassName} />
            <Input
              {...fieldProps("name")}
              placeholder="Hari Bahadur"
              className={cn(inputClassName, errors.name && "border-destructive")}
            />
          </div>
        </Field>

        <Field id="phone" label="Phone Number (Optional)" error={errors.phone}>
          <div className="relative">
            <MdPhone className={iconClassName} />
            <Input
              {...fieldProps("phone")}
              type="tel"
              placeholder="+977 9712345678"
              className={cn(inputClassName, errors.phone && "border-destructive")}
            />
          </div>
        </Field>
      </div>
      <Field id="email" label="Email Address" required error={errors.email}>
        <div className="relative">
          <MdEmail className={iconClassName} />
          <Input
            {...fieldProps("email")}
            type="email"
            placeholder="hari@example.com"
            className={cn(inputClassName, errors.email && "border-destructive")}
          />
        </div>
      </Field>
      <Field id="subject" label="Subject" required error={errors.subject}>
        <div className="relative">
          <MdMessage className={iconClassName} />
          <Input
            {...fieldProps("subject")}
            placeholder="Project Inquiry"
            className={cn(inputClassName, errors.subject && "border-destructive")}
          />
        </div>
      </Field>

      <Field id="message" label="Message" required error={errors.message}>
        <Textarea
          {...fieldProps("message")}
          rows={6}
          maxLength={MESSAGE_MAX_LENGTH}
          placeholder="Tell me about your project or inquiry..."
          className={cn(
            "transition-all duration-300 hover:border-teal-500 focus-visible:border-teal-500",
            errors.message && "border-destructive"
          )}
        />
        <p className="text-right text-xs text-muted-foreground">
          {values.message.length} / {MESSAGE_MAX_LENGTH}
        </p>
      </Field>

      <Button
        type="submit"
        size="lg"
        disabled={loading}
        className="w-full sm:w-auto transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/50"
      >
        {loading ? <AiOutlineLoading3Quarters className="animate-spin" /> : <MdEmail />}
        Send Message
      </Button>
    </form>
  )
}
